import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ChatBubbleLeftRightIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { testimonialsApi } from "../api/cmsApi";
import toast from "react-hot-toast";

interface Testimonial {
  _id: string;
  name: string;
  location: string;
  amount: number;
  quote: string;
  rating: number;
  variant: string | null;
  isActive: boolean;
}

const variants = [
  { id: null, label: "Alle Varianten" },
  { id: "default", label: "Standard" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "x", label: "X (Twitter)" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "reddit", label: "Reddit" },
];

interface FormData {
  name: string;
  location: string;
  amount: number;
  quote: string;
  rating: number;
  variant: string | null;
}

interface TestimonialFormProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function TestimonialForm({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  isLoading: formLoading,
}: TestimonialFormProps) {
  return (
    <div className="p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="z.B. Michael S."
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ort
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) =>
              setFormData({ ...formData, location: e.target.value })
            }
            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            placeholder="z.B. München"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Auszahlungsbetrag (EUR)
          </label>
          <input
            type="number"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
            }
            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            min="0"
            step="0.01"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bewertung
          </label>
          <div className="flex gap-1 mt-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setFormData({ ...formData, rating: star })}
                className="focus:outline-none"
              >
                {star <= formData.rating ? (
                  <StarIconSolid className="h-6 w-6 text-yellow-400" />
                ) : (
                  <StarIcon className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Zitat
        </label>
        <textarea
          value={formData.quote}
          onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
          className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          rows={3}
          placeholder="Was hat der Kunde gesagt?"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Variante (optional)
        </label>
        <select
          value={formData.variant || ""}
          onChange={(e) =>
            setFormData({ ...formData, variant: e.target.value || null })
          }
          className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        >
          {variants.map((v) => (
            <option key={v.id || "all"} value={v.id || ""}>
              {v.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Leer lassen, um auf allen Varianten anzuzeigen
        </p>
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Abbrechen
        </button>
        <button
          onClick={onSubmit}
          disabled={formLoading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
        >
          {formLoading ? "Speichern..." : "Speichern"}
        </button>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    amount: 0,
    quote: "",
    rating: 5,
    variant: null as string | null,
  });

  const { data: testimonials = [], isLoading } = useQuery({
    queryKey: ["testimonials"],
    queryFn: () => testimonialsApi.list().then((res) => res.data.data || []),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => testimonialsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      setIsCreating(false);
      resetForm();
      toast.success("Testimonial erstellt");
    },
    onError: () => toast.error("Fehler beim Erstellen"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Testimonial> }) =>
      testimonialsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      setEditingId(null);
      resetForm();
      toast.success("Testimonial aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => testimonialsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testimonials"] });
      toast.success("Testimonial gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  const resetForm = () => {
    setFormData({
      name: "",
      location: "",
      amount: 0,
      quote: "",
      rating: 5,
      variant: null,
    });
  };

  const startEditing = (testimonial: Testimonial) => {
    setEditingId(testimonial._id);
    setFormData({
      name: testimonial.name,
      location: testimonial.location,
      amount: testimonial.amount,
      quote: testimonial.quote,
      rating: testimonial.rating,
      variant: testimonial.variant,
    });
  };

  const handleToggleActive = (testimonial: Testimonial) => {
    updateMutation.mutate({
      id: testimonial._id,
      data: { isActive: !testimonial.isActive },
    });
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Testimonials
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie Kundenbewertungen und Erfahrungsberichte
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsCreating(true);
            resetForm();
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Neues Testimonial
        </button>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Neues Testimonial
            </h3>
          </div>
          <TestimonialForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={() => createMutation.mutate(formData)}
            onCancel={() => setIsCreating(false)}
            isLoading={createMutation.isPending}
          />
        </div>
      )}

      {/* Testimonials List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading ? (
          <div className="col-span-2 text-center py-12 text-gray-500 dark:text-gray-400">
            Laden...
          </div>
        ) : testimonials.length === 0 ? (
          <div className="col-span-2 bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Keine Testimonials vorhanden.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
            >
              Erstes Testimonial erstellen
            </button>
          </div>
        ) : (
          testimonials.map((testimonial: Testimonial) => (
            <div
              key={testimonial._id}
              className="bg-white dark:bg-gray-800 shadow rounded-lg"
            >
              {editingId === testimonial._id ? (
                <TestimonialForm
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={() =>
                    updateMutation.mutate({ id: testimonial._id, data: formData })
                  }
                  onCancel={() => setEditingId(null)}
                  isLoading={updateMutation.isPending}
                />
              ) : (
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {testimonial.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {testimonial.location}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(testimonial)}
                        className={`
                          px-2 py-1 rounded-full text-xs font-medium
                          ${
                            testimonial.isActive
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                          }
                        `}
                      >
                        {testimonial.isActive ? "Aktiv" : "Inaktiv"}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) =>
                      star <= testimonial.rating ? (
                        <StarIconSolid
                          key={star}
                          className="h-4 w-4 text-yellow-400"
                        />
                      ) : (
                        <StarIcon
                          key={star}
                          className="h-4 w-4 text-gray-300 dark:text-gray-600"
                        />
                      )
                    )}
                  </div>

                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    "{testimonial.quote}"
                  </p>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(testimonial.amount)}
                      </span>
                      {testimonial.variant && (
                        <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs px-2 py-0.5 rounded">
                          {
                            variants.find((v) => v.id === testimonial.variant)
                              ?.label
                          }
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => startEditing(testimonial)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Testimonial wirklich löschen?")) {
                            deleteMutation.mutate(testimonial._id);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
